// Settings → General: admin-only toggle for the WhatsApp bill-summary
// receipt (F-MM-N27). Off by default — sending also needs the tenant's
// WhatsApp number and a Meta-approved template, so the card says so plainly.
//
// messaging.config is a SINGLE JSON blob in app_settings shared with several
// unrelated fields (from_name, reply_to_email, whatsapp_sender_label,
// available_channels, auto_email_receipt_enabled, receipt_includes_coupon,
// review_url, receipt_coupon_percent). apiClient.updateSetting() does a PUT
// that REPLACES the whole value, so saving here must read-modify-write: take
// the latest fetched messaging.config, override only whatsapp_receipt_enabled,
// and send the merged object back. Sending a fresh object with only our one
// field would silently wipe every other messaging setting the tenant has
// configured — see mergeWhatsAppReceiptEnabled below.
//
// Admin-only render gate: this toggle spends a WhatsApp message on every
// completed order, a spending decision rather than a shift decision, so it
// is hidden from managers. This IS also enforced server-side (F-MM-N27):
// settings.go's UpdateSetting rejects a non-admin write to the
// `messaging.config` key with 403 insufficient_permissions, the same shape
// as po_approvers and the sibling KitchenFlowAlertsSettingCard's key. The two
// gates (UI render + server check) are both admin-only for the same reason —
// there is no asymmetry between them to justify relaxing either one.
import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import { useAllSettings } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'

export const MESSAGING_CONFIG_KEY = 'messaging.config'

/**
 * Reads whatsapp_receipt_enabled out of a messaging.config value of unknown
 * shape. A missing key, a missing row (undefined), or a malformed/non-object
 * value all read as false — the safe default, and the same "off" the backend
 * (messaging.LoadConfig) falls back to.
 */
export function whatsAppReceiptEnabledFrom(messagingConfig: unknown): boolean {
  if (messagingConfig && typeof messagingConfig === 'object' && !Array.isArray(messagingConfig)) {
    return (messagingConfig as Record<string, unknown>).whatsapp_receipt_enabled === true
  }
  return false
}

/**
 * Merges whatsapp_receipt_enabled into the CURRENT messaging.config without
 * disturbing any other field (from_name, reply_to_email, coupon settings,
 * …). `current` may be undefined (no row yet) or not a plain object (a
 * corrupt/legacy value) — in either case we start from an empty object
 * rather than throw, so the toggle still saves instead of crashing the page.
 */
export function mergeWhatsAppReceiptEnabled(current: unknown, enabled: boolean): Record<string, unknown> {
  const base =
    current && typeof current === 'object' && !Array.isArray(current)
      ? (current as Record<string, unknown>)
      : {}
  return { ...base, whatsapp_receipt_enabled: enabled }
}

export function WhatsAppReceiptSettingCard() {
  const all = useAllSettings()
  const stored = all?.[MESSAGING_CONFIG_KEY]
  const qc = useQueryClient()
  const { toast } = useToast()
  const [enabled, setEnabled] = useState<boolean>(() => whatsAppReceiptEnabledFrom(stored))
  useEffect(() => setEnabled(whatsAppReceiptEnabledFrom(stored)), [stored])

  const save = useMutation({
    mutationFn: (next: boolean) => apiClient.updateSetting(MESSAGING_CONFIG_KEY, mergeWhatsAppReceiptEnabled(stored, next)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      toast({ title: 'Settings saved', description: 'WhatsApp bill summary updated.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  if (getStoredUserRole() !== 'admin') return null

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">WhatsApp bill summary</CardTitle>
        <CardDescription>
          Sends after every completed order, to guests who gave a phone number. Costs a WhatsApp message each time.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-sm text-warning-fg">
          Needs a connected WhatsApp number for this restaurant, plus Meta's approval of the receipt template — messages will start once both are in place.
        </p>
        <div className="flex items-center gap-3">
          <Switch id="wa-receipt-enabled" checked={enabled} onCheckedChange={setEnabled} />
          <Label htmlFor="wa-receipt-enabled" className="text-base">Send the bill summary on WhatsApp</Label>
        </div>
        <Button className="h-11 px-6" disabled={save.isPending} onClick={() => save.mutate(enabled)}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}
